<script lang="ts">
  import { onMount } from "svelte";
  import { api } from "../lib/api";
  import { MAX_EDIT_SIZE } from "@antares/shared";
  import type { FileEntry } from "@antares/shared";
  import FileEditor from "./FileEditor.svelte";

  interface Props {
    ns: string;
    name: string;
    container: string;
    fsRoot?: string;
  }

  let { ns, name, container, fsRoot = "/" }: Props = $props();

  const root = $derived(fsRoot === "/" ? "/" : fsRoot.replace(/\/+$/, ""));

  let currentPath = $state(fsRoot);
  let entries = $state<FileEntry[]>([]);
  let loading = $state(false);
  let errorMsg = $state("");

  let editingFile = $state<string | null>(null);
  let fileContent = $state("");
  let largeFile = $state<{ path: string; size: number } | null>(null);
  let uploading = $state(false);
  let dragging = $state(false);
  let disk = $state<{ totalBytes: number; usedBytes: number; freeBytes: number } | null>(null);

  // Inline rename state
  let renamingPath = $state<string | null>(null);
  let renameValue = $state("");

  // Internal drag-move state
  let dragSourcePath = $state<string | null>(null);
  let dropTargetPath = $state<string | null>(null);

  onMount(() => {
    loadDirectory(root);
  });

  async function loadDirectory(path: string) {
    loading = true;
    errorMsg = "";
    try {
      const res = await api.listDirectory(ns, name, path, container);
      entries = res.entries;
      currentPath = res.path;
      disk = res.disk ?? null;
      editingFile = null;
      largeFile = null;
      renamingPath = null;
    } catch (err: any) {
      errorMsg = err.message;
    } finally {
      loading = false;
    }
  }

  async function openFile(entry: FileEntry) {
    if (renamingPath) return;
    if (entry.type === "directory") {
      await loadDirectory(entry.path);
      return;
    }
    loading = true;
    errorMsg = "";
    try {
      const res = await api.readFile(ns, name, entry.path, container);
      if (res.tooLarge) {
        largeFile = { path: res.path, size: res.size };
        editingFile = null;
      } else {
        fileContent = res.content ?? "";
        editingFile = entry.path;
        largeFile = null;
      }
    } catch (err: any) {
      errorMsg = err.message;
    } finally {
      loading = false;
    }
  }

  async function saveFile(path: string, content: string) {
    try {
      await api.writeFile(ns, name, path, content, container);
      fileContent = content;
    } catch (err: any) {
      errorMsg = `Save failed: ${err.message}`;
    }
  }

  const atRoot = $derived(currentPath === root);

  function navigateUp() {
    if (atRoot) return;
    const parent = currentPath.split("/").slice(0, -1).join("/") || "/";
    if (!parent.startsWith(root) && parent !== root) {
      loadDirectory(root);
    } else {
      loadDirectory(parent);
    }
  }

  function goBack() {
    editingFile = null;
    largeFile = null;
  }

  function downloadFile(path: string, ev?: MouseEvent) {
    ev?.stopPropagation();
    const url = api.downloadUrl(ns, name, path, container);
    const a = document.createElement("a");
    a.href = url;
    a.download = path.split("/").pop() ?? "download";
    a.click();
  }

  // --- New file ---
  async function createNewFile() {
    const fileName = prompt("New file name:", "untitled.txt");
    if (!fileName?.trim()) return;
    const filePath = currentPath.replace(/\/$/, "") + "/" + fileName.trim();
    try {
      await api.writeFile(ns, name, filePath, "", container);
      await loadDirectory(currentPath);
      // Open the new file in the editor
      fileContent = "";
      editingFile = filePath;
    } catch (err: any) {
      errorMsg = `Create failed: ${err.message}`;
    }
  }

  // --- Rename ---
  function startRename(entry: FileEntry, ev: MouseEvent) {
    ev.stopPropagation();
    renamingPath = entry.path;
    renameValue = entry.name;
  }

  async function commitRename() {
    if (!renamingPath || !renameValue.trim()) {
      renamingPath = null;
      return;
    }
    const dir = renamingPath.split("/").slice(0, -1).join("/");
    const newPath = dir + "/" + renameValue.trim();
    if (newPath === renamingPath) {
      renamingPath = null;
      return;
    }
    try {
      await api.moveFile(ns, name, renamingPath, newPath, container);
      renamingPath = null;
      await loadDirectory(currentPath);
    } catch (err: any) {
      errorMsg = `Rename failed: ${err.message}`;
      renamingPath = null;
    }
  }

  function handleRenameKeydown(ev: KeyboardEvent) {
    if (ev.key === "Enter") { ev.preventDefault(); commitRename(); }
    if (ev.key === "Escape") { renamingPath = null; }
  }

  // --- Move file up a directory ---
  async function moveUp(entry: FileEntry, ev: MouseEvent) {
    ev.stopPropagation();
    if (atRoot) return;
    const parentDir = currentPath.split("/").slice(0, -1).join("/") || "/";
    const dest = parentDir.replace(/\/$/, "") + "/" + entry.name;
    try {
      await api.moveFile(ns, name, entry.path, dest, container);
      await loadDirectory(currentPath);
    } catch (err: any) {
      errorMsg = `Move failed: ${err.message}`;
    }
  }

  // --- Internal drag to move into folder ---
  function handleInternalDragStart(ev: DragEvent, entry: FileEntry) {
    dragSourcePath = entry.path;
    ev.dataTransfer!.effectAllowed = "move";
    ev.dataTransfer!.setData("text/plain", entry.path);
  }

  function handleFolderDragOver(ev: DragEvent, entry: FileEntry) {
    if (!dragSourcePath || entry.type !== "directory") return;
    if (dragSourcePath === entry.path) return;
    ev.preventDefault();
    ev.dataTransfer!.dropEffect = "move";
    dropTargetPath = entry.path;
  }

  function handleFolderDragLeave() {
    dropTargetPath = null;
  }

  async function handleFolderDrop(ev: DragEvent, targetFolder: FileEntry) {
    if (!dragSourcePath) return; // let external drops bubble to handleExternalDrop
    ev.preventDefault();
    ev.stopPropagation();
    dropTargetPath = null;

    if (targetFolder.type !== "directory") return;
    const fileName = dragSourcePath.split("/").pop();
    const dest = targetFolder.path.replace(/\/$/, "") + "/" + fileName;
    try {
      await api.moveFile(ns, name, dragSourcePath, dest, container);
      dragSourcePath = null;
      await loadDirectory(currentPath);
    } catch (err: any) {
      errorMsg = `Move failed: ${err.message}`;
      dragSourcePath = null;
    }
  }

  function handleInternalDragEnd() {
    dragSourcePath = null;
    dropTargetPath = null;
  }

  // --- External file upload (drag from OS) ---
  async function uploadToDir(targetDir: string) {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = async () => {
      const files = input.files;
      if (!files || files.length === 0) return;
      await uploadFiles(targetDir, Array.from(files));
    };
    input.click();
  }

  async function handleUploadReplace(targetPath: string) {
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      uploading = true;
      errorMsg = "";
      try {
        await api.uploadFile(ns, name, targetPath, file, container);
        const dir = targetPath.split("/").slice(0, -1).join("/") || "/";
        await loadDirectory(dir);
      } catch (err: any) {
        errorMsg = `Upload failed: ${err.message}`;
      } finally {
        uploading = false;
      }
    };
    input.click();
  }

  async function uploadFiles(targetDir: string, files: File[]) {
    uploading = true;
    errorMsg = "";
    try {
      for (const file of files) {
        const targetPath = targetDir.replace(/\/$/, "") + "/" + file.name;
        await api.uploadFile(ns, name, targetPath, file, container);
      }
      await loadDirectory(targetDir);
    } catch (err: any) {
      errorMsg = `Upload failed: ${err.message}`;
    } finally {
      uploading = false;
    }
  }

  function handleExternalDragOver(ev: DragEvent) {
    // Only handle external drags (from OS), not internal row drags
    if (dragSourcePath) return;
    ev.preventDefault();
    dragging = true;
  }

  function handleExternalDragLeave(ev: DragEvent) {
    if (dragSourcePath) return;
    const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    const { clientX, clientY } = ev;
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
      dragging = false;
    }
  }

  async function handleExternalDrop(ev: DragEvent) {
    if (dragSourcePath) return; // internal drag handled by folder drop
    ev.preventDefault();
    dragging = false;
    const files = ev.dataTransfer?.files;
    if (!files || files.length === 0) return;
    await uploadFiles(currentPath, Array.from(files));
  }

  function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} K`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} M`;
  }

  function formatDate(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${months[d.getMonth()]} ${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  const maxEditLabel = formatSize(MAX_EDIT_SIZE);
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="file-explorer"
  class:drag-active={dragging}
  ondragover={handleExternalDragOver}
  ondragleave={handleExternalDragLeave}
  ondrop={handleExternalDrop}
  role="application"
>
  {#if editingFile}
    <div class="toolbar">
      <button class="btn-ghost" onclick={goBack}>&larr; Back</button>
      <span class="file-path">{editingFile}</span>
      <div class="toolbar-right">
        <button class="btn-ghost" onclick={(ev) => downloadFile(editingFile!, ev)}>Download</button>
      </div>
    </div>
    <FileEditor
      content={fileContent}
      filePath={editingFile}
      onSave={(content) => saveFile(editingFile!, content)}
    />
  {:else if largeFile}
    <div class="toolbar">
      <button class="btn-ghost" onclick={goBack}>&larr; Back</button>
      <span class="file-path">{largeFile.path}</span>
    </div>
    <div class="large-file-panel">
      <div class="large-file-info">
        <span class="large-file-size">{formatSize(largeFile.size)}</span>
        <p>This file exceeds the {maxEditLabel} edit limit.</p>
      </div>
      <div class="large-file-actions">
        <button class="btn-primary" onclick={() => downloadFile(largeFile!.path)}>
          Download
        </button>
        <button class="btn-secondary" onclick={() => handleUploadReplace(largeFile!.path)} disabled={uploading}>
          {uploading ? "Uploading..." : "Replace with upload"}
        </button>
      </div>
    </div>
  {:else}
    <div class="toolbar">
      <button class="btn-ghost" onclick={navigateUp} disabled={atRoot}>
        &uarr; Up
      </button>
      <span class="current-path">{currentPath}</span>
      <div class="toolbar-right">
        <button class="btn-ghost" onclick={createNewFile}>New file</button>
        <button class="btn-ghost" onclick={() => uploadToDir(currentPath)} disabled={uploading}>
          {uploading ? "Uploading..." : "Upload"}
        </button>
      </div>
    </div>

    {#if disk}
      {@const pct = Math.round((disk.usedBytes / disk.totalBytes) * 100)}
      <div class="disk-bar">
        <div class="disk-track">
          <div
            class="disk-fill"
            class:disk-warn={pct > 80}
            class:disk-danger={pct > 95}
            style="width: {pct}%"
          ></div>
        </div>
        <span class="disk-label">{formatSize(disk.usedBytes)} / {formatSize(disk.totalBytes)} ({pct}%)</span>
      </div>
    {/if}

    {#if errorMsg}
      <div class="error">{errorMsg}</div>
    {/if}

    {#if dragging}
      <div class="drop-overlay">
        <span>Drop files to upload to {currentPath}</span>
      </div>
    {/if}

    <div class="file-list">
      {#if loading}
        <div class="loading-msg">Loading...</div>
      {:else}
        {#each entries as entry (entry.path)}
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div
            class="file-row"
            class:drop-target={dropTargetPath === entry.path}
            draggable={entry.type === "file"}
            ondragstart={(ev) => handleInternalDragStart(ev, entry)}
            ondragend={handleInternalDragEnd}
            ondragover={(ev) => handleFolderDragOver(ev, entry)}
            ondragleave={handleFolderDragLeave}
            ondrop={(ev) => handleFolderDrop(ev, entry)}
          >
            <button class="file-row-main" onclick={() => openFile(entry)}>
              <span class="file-icon">
                {#if entry.type === "directory"}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1.5 2.5h4l1.5 1.5h7.5v9h-13v-10.5z" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>
                {:else if entry.size > MAX_EDIT_SIZE}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 1.5h5l3.5 3.5v9.5h-8.5v-13z" stroke="var(--warning)" stroke-width="1.2" fill="none"/><path d="M5.5 8h5M5.5 10.5h3" stroke="var(--warning)" stroke-width="1"/></svg>
                {:else}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 1.5h5l3.5 3.5v9.5h-8.5v-13z" stroke="currentColor" stroke-width="1.2" fill="none"/><path d="M5.5 8h5M5.5 10.5h3" stroke="currentColor" stroke-width="1"/></svg>
                {/if}
              </span>
              {#if renamingPath === entry.path}
                <!-- svelte-ignore a11y_autofocus -->
                <input
                  class="rename-input"
                  bind:value={renameValue}
                  onblur={commitRename}
                  onkeydown={handleRenameKeydown}
                  onclick={(ev) => ev.stopPropagation()}
                  autofocus
                />
              {:else}
                <span class="file-name" class:large-name={entry.type === "file" && entry.size > MAX_EDIT_SIZE}>{entry.name}</span>
              {/if}
              <span class="file-modified">{formatDate(entry.modified)}</span>
              <span class="file-size">{entry.type === "file" ? formatSize(entry.size) : "\u2014"}</span>
            </button>
            <div class="row-actions">
              {#if entry.type === "file" && !atRoot}
                <button class="btn-action" title="Move up a directory" onclick={(ev) => moveUp(entry, ev)}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 9V3M3.5 5.5L6 3l2.5 2.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              {/if}
              <button class="btn-action" title="Rename" onclick={(ev) => startRename(entry, ev)}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8.5 1.5l2 2-7 7H1.5V8.5l7-7z" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              {#if entry.type === "file"}
                <button class="btn-action" title="Download" onclick={(ev) => downloadFile(entry.path, ev)}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 2v6M3.5 6L6 8.5 8.5 6M2 10.5h8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              {/if}
            </div>
          </div>
        {/each}
      {/if}
    </div>
  {/if}
</div>

<style>
  .file-explorer {
    border: 1px solid var(--border);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
  }

  .file-explorer.drag-active {
    border-color: var(--accent);
  }

  .toolbar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border);
  }

  .toolbar-right {
    margin-left: auto;
    display: flex;
    gap: 0.4rem;
  }

  .current-path, .file-path {
    font-family: var(--font-mono);
    font-size: 0.8rem;
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .btn-ghost {
    background: none;
    border: 1px solid var(--border);
    color: var(--text-muted);
    padding: 0.2rem 0.5rem;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .btn-ghost:hover:not(:disabled) { color: var(--text); }
  .btn-ghost:disabled { opacity: 0.4; cursor: not-allowed; }

  .disk-bar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.35rem 0.75rem;
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border);
  }

  .disk-track {
    flex: 1;
    height: 6px;
    background: var(--bg-elevated);
    border-radius: 3px;
    overflow: hidden;
  }

  .disk-fill {
    height: 100%;
    background: var(--accent);
    border-radius: 3px;
    transition: width 0.3s;
  }

  .disk-fill.disk-warn { background: var(--warning); }
  .disk-fill.disk-danger { background: var(--danger); }

  .disk-label {
    font-size: 0.65rem;
    font-family: var(--font-mono);
    color: var(--text-muted);
    white-space: nowrap;
  }

  .error {
    padding: 0.5rem 0.75rem;
    color: var(--danger);
    font-size: 0.8rem;
    background: rgba(248, 113, 113, 0.05);
  }

  .loading-msg {
    padding: 2rem;
    text-align: center;
    color: var(--text-muted);
  }

  .drop-overlay {
    position: absolute;
    inset: 0;
    background: rgba(108, 140, 255, 0.08);
    border: 2px dashed var(--accent);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    pointer-events: none;
  }

  .drop-overlay span {
    background: var(--bg-surface);
    padding: 0.5rem 1rem;
    border-radius: 4px;
    color: var(--accent);
    font-size: 0.85rem;
  }

  .file-list {
    max-height: 460px;
    overflow-y: auto;
  }

  .file-row {
    display: flex;
    align-items: center;
    border-bottom: 1px solid var(--border);
    transition: background 0.1s;
  }

  .file-row:last-child {
    border-bottom: none;
  }

  .file-row:hover {
    background: var(--bg-elevated);
  }

  .file-row.drop-target {
    background: rgba(108, 140, 255, 0.12);
    outline: 1px dashed var(--accent);
    outline-offset: -1px;
  }

  .file-row-main {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    padding: 0.4rem 0.5rem 0.4rem 0.75rem;
    background: none;
    border: none;
    color: var(--text);
    cursor: pointer;
    text-align: left;
    gap: 0.5rem;
    font-size: 0.8rem;
  }

  .row-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.15rem;
    padding-right: 0.5rem;
    width: 4.5rem;
    flex-shrink: 0;
    opacity: 0;
    transition: opacity 0.1s;
  }

  .file-row:hover .row-actions { opacity: 1; }

  .btn-action {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 0.3rem;
    border-radius: 3px;
  }

  .btn-action:hover { color: var(--accent); background: rgba(108, 140, 255, 0.1); }

  .rename-input {
    flex: 1;
    background: var(--bg);
    border: 1px solid var(--accent);
    border-radius: 3px;
    color: var(--text);
    font-family: var(--font-mono);
    font-size: 0.8rem;
    padding: 0.1rem 0.4rem;
    outline: none;
  }

  .file-icon { width: 1.5rem; text-align: center; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .file-name { flex: 1; font-family: var(--font-mono); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .file-name.large-name { color: var(--warning); }
  .file-modified { width: 7.5rem; text-align: right; color: var(--text-muted); font-family: var(--font-mono); font-size: 0.7rem; flex-shrink: 0; }
  .file-size { width: 5rem; text-align: right; color: var(--text-muted); font-family: var(--font-mono); flex-shrink: 0; }

  .large-file-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    gap: 1.5rem;
    text-align: center;
  }

  .large-file-info p {
    color: var(--text-muted);
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }

  .large-file-size {
    font-size: 1.5rem;
    font-weight: 600;
    font-family: var(--font-mono);
    color: var(--warning);
  }

  .large-file-actions {
    display: flex;
    gap: 0.75rem;
  }

  .btn-primary {
    padding: 0.5rem 1.25rem;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85rem;
  }

  .btn-primary:hover { background: var(--accent-hover); }

  .btn-secondary {
    padding: 0.5rem 1.25rem;
    background: none;
    border: 1px solid var(--border);
    color: var(--text);
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85rem;
  }

  .btn-secondary:hover:not(:disabled) { border-color: var(--text-muted); }
  .btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
